/**
 * GSAP Animation System with ScrollTrigger
 *
 * Advanced animation system using GSAP and ScrollTrigger for smooth,
 * performant animations with scroll-based triggers.
 *
 * @package Alliant
 * @since 2.0.0
 */
console.log("GSAP Animation System");
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, SplitText, ScrollToPlugin);

class GSAPAnimationSystem {
  constructor() {
    this.animations = new Map();
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        this.setupAnimations()
      );
    } else {
      this.setupAnimations();
    }
  }

  setupAnimations() {
    // Ensure ScrollTrigger proxy is set up before creating any ScrollTrigger instances
    // This ensures Lenis smooth scroll works correctly with ScrollTrigger
    // Check for smooth scroll instance in multiple ways to handle timing
    const smoothScrollInstance = window.SOJ_SMOOTH_SCROLL || 
                                 (window.smoothScroll && typeof window.smoothScroll.setupScrollTriggerProxy === 'function' ? window.smoothScroll : null);
    
    if (smoothScrollInstance && typeof smoothScrollInstance.setupScrollTriggerProxy === 'function') {
      smoothScrollInstance.setupScrollTriggerProxy();
    }

    // Set up default animations
    this.setupFadeInAnimations();
    this.setupSlideInAnimations();
    this.setupStaggerAnimations();
    this.setupSlowStaggerAnimations();
    this.setupScaleAnimations();
    this.initHighlightText();
    this.setupDataAttributeAnimations();

    // Refresh ScrollTrigger to ensure all animations are registered and use the proxy
    // Use a small delay to ensure proxy is fully configured
    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });

    console.log(
      "[GSAP] Animation system initialized with ScrollTrigger and SplitText"
    );
  }

  /**
   * Handle data-gsap-animate attributes globally
   */
  setupDataAttributeAnimations() {
    const elements = document.querySelectorAll("[data-gsap-animate]");

    elements.forEach((element) => {
      // Skip hero content and elements with data-no-animate
      if (
        element.closest(".header-full-screen") ||
        element.hasAttribute("data-no-animate")
      ) {
        return;
      }
      const animationType = element.dataset.gsapAnimate;
      const delay = (parseInt(element.dataset.gsapDelay) || 0) / 1000;

      // Animation configurations
      const animations = {
        "fade-in": {
            from: { opacity: 0 },
          to: { opacity: 1, duration: 3, ease: "power2.out" },
        },
        "slide-left": {
          from: { x: -100, opacity: 0 },
          to: { x: 0, opacity: 1, duration: 1, ease: "power2.out" },
        },
        "slide-right": {
          from: { x: 100, opacity: 0 },
          to: { x: 0, opacity: 1, duration: 1, ease: "power2.out" },
        },
        "slide-up": {
          from: { y: 100, opacity: 0 },
          to: { y: 0, opacity: 1, duration: 1, ease: "power2.out" },
        },
        "slide-down": {
          from: { y: -100, opacity: 0 },
          to: { y: 0, opacity: 1, duration: 1, ease: "power2.out" },
        },
        scale: {
          from: { scale: 0.8, opacity: 0 },
          to: { scale: 1, opacity: 1, duration: 1, ease: "back.out(1.7)" },
        },
        rotate: {
          from: { rotation: -180, opacity: 0 },
          to: { rotation: 0, opacity: 1, duration: 1, ease: "power2.out" },
        },
      };

      if (animationType === "stagger") {
        // Stagger animation for children
        const children = element.children;
        if (children.length > 0) {
          gsap.fromTo(
            children,
            { opacity: 0, y: -50 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              delay: delay,
              stagger: 0.1,
              ease: "power2.out",
              onComplete: () => element.classList.add("gsap-animated"),
              scrollTrigger: {
                trigger: element,
                start: "top 80%",
                toggleActions: "play none none none",
              },
            }
          );
          this.animations.set(element, element);
        }
      } else if (animations[animationType]) {
        // Apply standard animation
        const config = animations[animationType];
        gsap.fromTo(element, config.from, {
          ...config.to,
          delay: delay,
          onComplete: () => element.classList.add("gsap-animated"),
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        });
        this.animations.set(element, element);
      }
    });
  }

  /**
   * Fade in animations
   */
  setupFadeInAnimations() {
    const fadeElements = document.querySelectorAll(".gsap-fade-in");

    fadeElements.forEach((element) => {
      // Skip hero content and elements with data-no-animate
      if (
        element.closest(".header-full-screen") ||
        element.hasAttribute("data-no-animate")
      ) {
        return;
      }
      // Check for delay classes
      const delay = this.getDelayFromClasses(element);

      const animation = gsap.fromTo(
        element,
        {
          opacity: 0,
          y: -50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: delay,
          ease: "power2.out",
          onComplete: () => element.classList.add("gsap-animated"),
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        }
      );

      this.animations.set(element, animation);
    });
  }

  /**
   * Slide in animations from different directions
   */
  setupSlideInAnimations() {
    // Slide from left
    const slideLeftElements = document.querySelectorAll(".gsap-slide-left");
    slideLeftElements.forEach((element) => {
      // Skip hero content and elements with data-no-animate
      if (
        element.closest(".header-full-screen") ||
        element.hasAttribute("data-no-animate")
      ) {
        return;
      }
      const delay = this.getDelayFromClasses(element);
      const animation = gsap.fromTo(
        element,
        {
          x: -100,
          opacity: 0,
        },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          delay: delay,
          ease: "power2.out",
          onComplete: () => element.classList.add("gsap-animated"),
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        }
      );
      this.animations.set(element, animation);
    });

    // Slide from right
    const slideRightElements = document.querySelectorAll(".gsap-slide-right");
    slideRightElements.forEach((element) => {
      // Skip hero content and elements with data-no-animate
      if (
        element.closest(".header-full-screen") ||
        element.hasAttribute("data-no-animate")
      ) {
        return;
      }
      const delay = this.getDelayFromClasses(element);
      const animation = gsap.fromTo(
        element,
        {
          x: 100,
          opacity: 0,
        },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          delay: delay,
          ease: "power2.out",
          onComplete: () => element.classList.add("gsap-animated"),
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        }
      );
      this.animations.set(element, animation);
    });

    // Slide from bottom
    const slideUpElements = document.querySelectorAll(".gsap-slide-up");
    slideUpElements.forEach((element) => {
      // Skip hero content and elements with data-no-animate
      if (
        element.closest(".header-full-screen") ||
        element.hasAttribute("data-no-animate")
      ) {
        return;
      }
      const delay = this.getDelayFromClasses(element);
      const animation = gsap.fromTo(
        element,
        {
          y: 100,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          delay: delay,
          ease: "power2.out",
          onComplete: () => element.classList.add("gsap-animated"),
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        }
      );
      this.animations.set(element, animation);
    });

    // Slide from top
    const slideDownElements = document.querySelectorAll(".gsap-slide-down");
    slideDownElements.forEach((element) => {
      // Skip hero content and elements with data-no-animate
      if (
        element.closest(".header-full-screen") ||
        element.hasAttribute("data-no-animate")
      ) {
        return;
      }
      const delay = this.getDelayFromClasses(element);
      const animation = gsap.fromTo(
        element,
        {
          y: -100,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          delay: delay,
          ease: "power2.out",
          onComplete: () => element.classList.add("gsap-animated"),
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        }
      );
      this.animations.set(element, animation);
    });
  }

  /**
   * Stagger animations for lists and grids
   */
  setupStaggerAnimations() {
    const staggerElements = document.querySelectorAll(".gsap-stagger");

    staggerElements.forEach((element) => {
      // Skip hero content and elements with data-no-animate
      if (
        element.closest(".header-full-screen") ||
        element.hasAttribute("data-no-animate")
      ) {
        return;
      }
      const children = element.children;

      if (children.length > 0) {
        const animation = gsap.fromTo(
          children,
          {
            opacity: 0,
            y: -50,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power2.out",
          onComplete: () => element.classList.add("gsap-animated"),
            scrollTrigger: {
              trigger: element,
              start: "top 80%",
              toggleActions: "play none none none",
            },
          }
        );

        this.animations.set(element, animation);
      }
    });
  }

  /**
   * Slow stagger animations for lists and grids
   */
  setupSlowStaggerAnimations() {
    const slowStaggerElements = document.querySelectorAll(".gsap-stagger-slow");

    slowStaggerElements.forEach((element) => {
      // Skip hero content and elements with data-no-animate
      if (
        element.closest(".header-full-screen") ||
        element.hasAttribute("data-no-animate")
      ) {
        return;
      }
      const children = element.children;

      if (children.length > 0) {
        const animation = gsap.fromTo(
          children,
          {
            opacity: 0,
            y: -50,
          },
          {
            opacity: 1,
            y: 0,
            duration: 1.2,
            stagger: 0.3,
            ease: "power2.out",
          onComplete: () => element.classList.add("gsap-animated"),
            scrollTrigger: {
              trigger: element,
              start: "top 80%",
              toggleActions: "play none none none",
            },
          }
        );

        this.animations.set(element, animation);
      }
    });
  }

  /**
   * Scale animations
   */
  setupScaleAnimations() {
    const scaleElements = document.querySelectorAll(".gsap-scale");

    scaleElements.forEach((element) => {
      // Skip hero content and elements with data-no-animate
      if (
        element.closest(".header-full-screen") ||
        element.hasAttribute("data-no-animate")
      ) {
        return;
      }
      const delay = this.getDelayFromClasses(element);
      const animation = gsap.fromTo(
        element,
        {
          scale: 0.8,
          opacity: 0,
        },
        {
          scale: 1,
          opacity: 1,
          duration: 1,
          delay: delay,
          ease: "back.out(1.7)",
          onComplete: () => element.classList.add("gsap-animated"),
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        }
      );

      this.animations.set(element, animation);
    });
  }

  /**
   * Highlight text on scroll animation
   * Uses data-highlight-text attribute to find and animate text elements
   */
  initHighlightText() {
    let splitHeadingTargets = document.querySelectorAll(
      "[data-highlight-text]"
    );
    console.log(
      "[GSAP] initHighlightText called, found elements:",
      splitHeadingTargets.length
    );
    splitHeadingTargets.forEach((heading) => {
      // Skip hero content and elements with data-no-animate
      if (
        heading.closest(".header-full-screen") ||
        heading.hasAttribute("data-no-animate")
      ) {
        return;
      }
      console.log("[GSAP] Processing highlight text element:", heading);
      const scrollStart =
        heading.getAttribute("data-highlight-scroll-start") || "top 90%";
      const scrollEnd =
        heading.getAttribute("data-highlight-scroll-end") || "center 40%";
      const fadedValue = heading.getAttribute("data-highlight-fade") || 0.2; // Opacity of letter
      const staggerValue =
        heading.getAttribute("data-highlight-stagger") || 0.1; // Smoother reveal

      new SplitText(heading, {
        type: "words, chars",
        autoSplit: true,
        // "auto" adds aria-label to the container, which is invalid on a generic <div>.
        aria: "none",
        onSplit(self) {
          let ctx = gsap.context(() => {
            let tl = gsap.timeline({
              scrollTrigger: {
                scrub: true,
                trigger: heading,
                start: scrollStart,
                end: scrollEnd,
              },
            });
            tl.from(self.chars, {
              autoAlpha: fadedValue,
              stagger: staggerValue,
              ease: "linear",
            });
          });
          return ctx; // return our animations so GSAP can clean them up when onSplit fires
        },
      });
    });
  }

  /**
   * Create custom animation
   */
  createCustomAnimation(
    element,
    fromProps,
    toProps,
    scrollTriggerOptions = {}
  ) {
    const defaultScrollTrigger = {
      trigger: element,
      start: "top 80%",
      toggleActions: "play none none reverse",
    };

    const animation = gsap.fromTo(element, fromProps, {
      ...toProps,
      scrollTrigger: {
        ...defaultScrollTrigger,
        ...scrollTriggerOptions,
      },
    });

    this.animations.set(element, animation);
    return animation;
  }

  /**
   * Refresh all animations (useful for dynamic content)
   */
  refresh() {
    ScrollTrigger.refresh();
    console.log("[GSAP] Animations refreshed");
  }

  /**
   * Kill all animations
   */
  killAll() {
    // Kill all stored animations
    this.animations.forEach((animation) => {
      if (animation && typeof animation.kill === "function") {
        animation.kill();
      }
    });
    this.animations.clear();

    // Kill all ScrollTriggers
    if (typeof ScrollTrigger !== "undefined" && ScrollTrigger.getAll) {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    }

    // Kill all GSAP tweens
    if (typeof gsap !== "undefined") {
      gsap.killTweensOf("*");
    }

    console.log("[GSAP] All animations killed");
  }

  /**
   * Get animation for specific element
   */
  getAnimation(element) {
    return this.animations.get(element);
  }

  /**
   * Get delay value from CSS classes
   */
  getDelayFromClasses(element) {
    const classList = element.classList;

    // Check for delay classes (in seconds)
    if (classList.contains("gsap-delay-0")) return 0;
    if (classList.contains("gsap-delay-100")) return 0.1;
    if (classList.contains("gsap-delay-200")) return 0.2;
    if (classList.contains("gsap-delay-300")) return 0.3;
    if (classList.contains("gsap-delay-400")) return 0.4;
    if (classList.contains("gsap-delay-500")) return 0.5;
    if (classList.contains("gsap-delay-600")) return 0.6;
    if (classList.contains("gsap-delay-700")) return 0.7;
    if (classList.contains("gsap-delay-800")) return 0.8;
    if (classList.contains("gsap-delay-900")) return 0.9;
    if (classList.contains("gsap-delay-1000")) return 1.0;
    if (classList.contains("gsap-delay-1200")) return 1.2;
    if (classList.contains("gsap-delay-1500")) return 1.5;
    if (classList.contains("gsap-delay-2000")) return 2.0;
    if (classList.contains("gsap-delay-2500")) return 2.5;
    if (classList.contains("gsap-delay-3000")) return 3.0;

    return 0; // No delay
  }
}

// Export the class
export default GSAPAnimationSystem;
